import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Upload,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Download,
  Calendar,
  Loader2,
  ChevronDown,
  ChevronRight,
  History,
  Wallet,
  Receipt,
  AlertTriangle,
  ChevronUp,
  TrendingDown,
} from 'lucide-react';
import {
  fetchMyCurrentAccount,
  fetchMyFinancialAccounts,
  fetchMyPayments,
  fetchMyBankTransfers,
  downloadMyReceipt,
  formatCurrency,
  formatPaymentMethod,
  formatTransferStatus,
  getStatusColor,
  type StudentFinancialAccount,
  type StudentPayment,
  type PendingBankTransfer,
  type CurrentAccountResponse,
  type MyPaymentsResponse,
} from '@/services/financialService';
import { BankTransferUploadModal } from '../components/BankTransferUploadModal';

// Origin (no /api) for stored files under /uploads on the API host.
const API_ORIGIN = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';

export const MyPaymentsPage: React.FC = () => {
  const [currentAccount, setCurrentAccount] = useState<CurrentAccountResponse | null>(null);
  const [allAccounts, setAllAccounts] = useState<StudentFinancialAccount[]>([]);
  const [paymentsData, setPaymentsData] = useState<MyPaymentsResponse | null>(null);
  const [pendingTransfers, setPendingTransfers] = useState<PendingBankTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [expandedYears, setExpandedYears] = useState<string[]>([]);
  const [showFeeDetails, setShowFeeDetails] = useState(false);
  // Which payment's receipt is currently being generated/downloaded.
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<string | null>(null);

  // The receipt PDF is generated on demand server-side, so show progress and
  // let the browser save it straight to Downloads (a.download in the helper).
  const handleDownloadReceipt = async (paymentId: string, receiptNumber?: string): Promise<void> => {
    setDownloadingReceiptId(paymentId);
    try {
      await downloadMyReceipt(paymentId, `recu-${receiptNumber || paymentId}.pdf`);
    } catch {
      window.alert('Reçu indisponible pour ce paiement');
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [currentData, accountsData, payments, transfers] = await Promise.all([
        fetchMyCurrentAccount(),
        fetchMyFinancialAccounts(),
        fetchMyPayments(),
        fetchMyBankTransfers(),
      ]);

      // "Année en cours" is exactly the year the calendar says we are in —
      // never a fallback to the most recent old account, which used to show
      // last year's balance under this year's heading. Past years live in
      // the "Historique" tab.
      setCurrentAccount(currentData);

      setAllAccounts(accountsData);
      setPaymentsData(payments);
      setPendingTransfers(transfers);
    } catch (error) {
      console.error('Error loading payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = (accountId: string) => {
    setSelectedAccountId(accountId);
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    loadData();
  };

  const toggleYear = (yearName: string) => {
    setExpandedYears(prev =>
      prev.includes(yearName)
        ? prev.filter(y => y !== yearName)
        : [...prev, yearName]
    );
  };

  const formatShortDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getInstallmentStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'PARTIAL':
        return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      case 'PENDING':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getInstallmentStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'Payé';
      case 'PARTIAL':
        return 'Partiel';
      case 'OVERDUE':
        return 'En retard';
      case 'PENDING':
        return 'En attente';
      case 'CANCELLED':
        return 'Annulé';
      default:
        return status;
    }
  };

  // Calculate payment status information from schedule
  const getPaymentStatusInfo = (schedule: StudentFinancialAccount['schedule']) => {
    if (!schedule || !schedule.installments || schedule.installments.length === 0) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const installments = schedule.installments;

    // Find overdue installments (due date passed and not fully paid)
    const overdueInstallments = installments.filter(inst => {
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today && inst.status !== 'PAID';
    });

    // Calculate total overdue amount
    const totalOverdue = overdueInstallments.reduce((sum, inst) => {
      return sum + (inst.amountDue - inst.amountPaid);
    }, 0);

    // Find next upcoming payment (first unpaid/partial installment that's not overdue)
    const upcomingInstallments = installments.filter(inst => {
      const dueDate = new Date(inst.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate >= today && inst.status !== 'PAID';
    });
    const nextPayment = upcomingInstallments.length > 0 ? upcomingInstallments[0] : null;

    // Calculate days until next payment
    let daysUntilNext: number | null = null;
    if (nextPayment) {
      const nextDueDate = new Date(nextPayment.dueDate);
      nextDueDate.setHours(0, 0, 0, 0);
      daysUntilNext = Math.ceil((nextDueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    }

    // Calculate days overdue for the oldest overdue payment
    let daysOverdue = 0;
    if (overdueInstallments.length > 0) {
      const oldestOverdue = overdueInstallments[0];
      const oldestDueDate = new Date(oldestOverdue.dueDate);
      oldestDueDate.setHours(0, 0, 0, 0);
      daysOverdue = Math.ceil((today.getTime() - oldestDueDate.getTime()) / (1000 * 60 * 60 * 24));
    }

    return {
      overdueInstallments,
      totalOverdue,
      overdueCount: overdueInstallments.length,
      daysOverdue,
      nextPayment,
      daysUntilNext,
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const account = currentAccount?.account;
  const academicYear = currentAccount?.academicYear || account?.academicYear;
  const paymentStatus = account?.schedule ? getPaymentStatusInfo(account.schedule) : null;

  // Separate fee items and discounts for display
  const feeItems = account?.feeItems || [];
  const discounts = account?.discounts || [];

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon Compte Financier</h1>
          {academicYear && (
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Année académique {academicYear.name}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {activeTab === 'current' && account && account.balance > 0 && (
            <button
              onClick={() => handleUploadClick(account.id)}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all"
            >
              <Upload className="w-4 h-4" />
              Soumettre un virement
            </button>
          )}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('current')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'current'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Année en cours
              </span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Historique
              </span>
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'current' && (
        <>
          {currentAccount?.hasAccount && account ? (
            <div className="space-y-6">
              {/* ========== SECTION 1: FINANCIAL SUMMARY CARDS ========== */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Net à payer - Expandable with details */}
                <div
                  className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-blue-300 transition-colors"
                  onClick={() => setShowFeeDetails(!showFeeDetails)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Net à payer</span>
                    </div>
                    {showFeeDetails ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(account.totalDue - account.totalDiscount)}</p>
                  <p className="text-xs text-blue-600 mt-1">Cliquez pour voir le détail</p>
                </div>

                {/* Déjà payé */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-500">Déjà payé</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600 mt-2">{formatCurrency(account.totalPaid)}</p>
                </div>

                {/* En retard */}
                {paymentStatus && paymentStatus.totalOverdue > 0 ? (
                  <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-sm text-red-600">En retard</span>
                    </div>
                    <p className="text-2xl font-bold text-red-700 mt-2">{formatCurrency(paymentStatus.totalOverdue)}</p>
                    <p className="text-xs text-red-500 mt-1">{paymentStatus.overdueCount} échéance{paymentStatus.overdueCount > 1 ? 's' : ''}</p>
                  </div>
                ) : (
                  <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-600">En retard</span>
                    </div>
                    <p className="text-2xl font-bold text-green-700 mt-2">0 MAD</p>
                    <p className="text-xs text-green-500 mt-1">Aucun retard</p>
                  </div>
                )}

                {/* Reste à payer */}
                <div className={`rounded-xl border p-4 ${
                  account.balance > 0
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <Wallet className={`w-4 h-4 ${account.balance > 0 ? 'text-amber-500' : 'text-green-500'}`} />
                    <span className={`text-sm ${account.balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {account.balance > 0 ? 'Reste à payer' : 'Soldé'}
                    </span>
                  </div>
                  <p className={`text-2xl font-bold mt-2 ${account.balance > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                    {formatCurrency(account.balance)}
                  </p>
                </div>
              </div>

              {/* ========== EXPANDABLE FEE DETAILS ========== */}
              {showFeeDetails && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden animate-in slide-in-from-top-2 duration-200">
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-gray-500" />
                      Détail des frais et réductions
                    </h3>
                  </div>
                  <div className="p-5">
                    {/* Fee Items */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Frais de scolarité</p>
                      {feeItems.length > 0 ? (
                        feeItems.map((fee) => (
                          <div key={fee.id} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${
                                fee.type === 'REGISTRATION' ? 'bg-blue-500' :
                                fee.type === 'TUITION' ? 'bg-indigo-500' :
                                'bg-gray-400'
                              }`} />
                              <span className="text-gray-700">{fee.label}</span>
                            </div>
                            <span className="font-semibold text-gray-900">{formatCurrency(fee.amount)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-700">Frais de scolarité</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(account.totalDue)}</span>
                        </div>
                      )}
                    </div>

                    {/* Discounts */}
                    {discounts.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Réductions / Bourses</p>
                        {discounts.map((discount) => (
                          <div key={discount.id} className="flex justify-between items-center py-2 px-3 bg-purple-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <TrendingDown className="w-4 h-4 text-purple-500" />
                              <span className="text-gray-700">{discount.discountConfig?.name || 'Réduction'}</span>
                            </div>
                            <span className="font-semibold text-purple-600">-{formatCurrency(discount.discountAmount)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Total */}
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                      <span className="font-semibold text-gray-700">Net à payer</span>
                      <span className="text-xl font-bold text-gray-900">{formatCurrency(account.totalDue - account.totalDiscount)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ========== SECTION 2: PAYMENT SCHEDULE TABLE ========== */}
              {account.schedule && account.schedule.installments.length > 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Header */}
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <div>
                        <h3 className="font-semibold text-gray-900">Échéancier de paiement</h3>
                        <p className="text-xs text-gray-500">{account.schedule.template?.name || 'Plan de paiement personnalisé'}</p>
                      </div>
                    </div>

                    {/* Status Badge */}
                    {paymentStatus && paymentStatus.totalOverdue > 0 ? (
                      <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1.5 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-semibold">{formatCurrency(paymentStatus.totalOverdue)} en retard</span>
                      </div>
                    ) : account.balance > 0 && paymentStatus?.nextPayment ? (
                      <div className="flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                          Prochain: {formatCurrency(paymentStatus.nextPayment.amountDue - paymentStatus.nextPayment.amountPaid)}
                        </span>
                      </div>
                    ) : account.balance === 0 ? (
                      <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-lg">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">Soldé</span>
                      </div>
                    ) : null}
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Échéance</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date limite</th>
                          <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant dû</th>
                          <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Payé</th>
                          <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Reste</th>
                          <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {account.schedule.installments.map((installment, index) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dueDate = new Date(installment.dueDate);
                          dueDate.setHours(0, 0, 0, 0);
                          const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                          const isOverdue = diffDays < 0 && installment.status !== 'PAID';
                          const isUpcoming = diffDays >= 0 && diffDays <= 7 && installment.status !== 'PAID';
                          const remainingAmount = installment.amountDue - installment.amountPaid;

                          // Check if first installment includes registration fee (compare with other installments)
                          const isFirstInstallment = index === 0;
                          const otherInstallments = account.schedule?.installments.slice(1) || [];
                          const avgOtherAmount = otherInstallments.length > 0
                            ? otherInstallments.reduce((sum, i) => sum + i.amountDue, 0) / otherInstallments.length
                            : 0;
                          const hasRegistrationFee = isFirstInstallment && installment.amountDue > avgOtherAmount * 1.1;
                          const estimatedRegistrationFee = hasRegistrationFee ? installment.amountDue - avgOtherAmount : 0;

                          return (
                            <tr
                              key={installment.id}
                              className={`${
                                installment.status === 'PAID'
                                  ? 'bg-green-50/50'
                                  : isOverdue
                                  ? 'bg-red-50'
                                  : isUpcoming
                                  ? 'bg-amber-50/50'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    installment.status === 'PAID'
                                      ? 'bg-green-500 text-white'
                                      : isOverdue
                                      ? 'bg-red-500 text-white'
                                      : isUpcoming
                                      ? 'bg-amber-500 text-white'
                                      : 'bg-gray-200 text-gray-600'
                                  }`}>
                                    {installment.status === 'PAID' ? (
                                      <CheckCircle className="w-4 h-4" />
                                    ) : isOverdue ? (
                                      <AlertTriangle className="w-4 h-4" />
                                    ) : (
                                      installment.sequenceNumber
                                    )}
                                  </div>
                                  <span className="font-medium text-gray-900">{installment.label}</span>
                                </div>
                              </td>
                              <td className="px-5 py-4">
                                <div>
                                  <p className="text-sm text-gray-900">{formatShortDate(installment.dueDate)}</p>
                                  {installment.status !== 'PAID' && (
                                    <p className={`text-xs ${
                                      isOverdue ? 'text-red-600' : isUpcoming ? 'text-amber-600' : 'text-gray-500'
                                    }`}>
                                      {isOverdue
                                        ? `${Math.abs(diffDays)} jour${Math.abs(diffDays) > 1 ? 's' : ''} de retard`
                                        : diffDays === 0
                                        ? "Aujourd'hui"
                                        : diffDays === 1
                                        ? 'Demain'
                                        : `Dans ${diffDays} jours`
                                      }
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-5 py-4 text-right">
                                <div>
                                  <span className="font-semibold text-gray-900">{formatCurrency(installment.amountDue)}</span>
                                  {hasRegistrationFee && estimatedRegistrationFee > 0 && (
                                    <p className="text-xs text-blue-600 mt-0.5">
                                      Inclut frais d'inscription
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-5 py-4 text-right">
                                <span className={`font-semibold ${installment.amountPaid > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                  {formatCurrency(installment.amountPaid)}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-right">
                                <span className={`font-bold ${
                                  remainingAmount === 0 ? 'text-green-600' : isOverdue ? 'text-red-600' : 'text-gray-900'
                                }`}>
                                  {formatCurrency(remainingAmount)}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-center">
                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                                  installment.status === 'PAID'
                                    ? 'bg-green-100 text-green-700'
                                    : isOverdue
                                    ? 'bg-red-100 text-red-700'
                                    : installment.status === 'PARTIAL'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : isUpcoming
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {installment.status === 'PAID'
                                    ? 'Payé'
                                    : isOverdue
                                    ? 'En retard'
                                    : installment.status === 'PARTIAL'
                                    ? 'Partiel'
                                    : isUpcoming
                                    ? 'À venir'
                                    : 'Planifié'
                                  }
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {/* Footer totals */}
                      <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                        <tr>
                          <td colSpan={2} className="px-5 py-3 font-semibold text-gray-700">Total</td>
                          <td className="px-5 py-3 text-right font-bold text-gray-900">
                            {formatCurrency(account.schedule.installments.reduce((sum, i) => sum + i.amountDue, 0))}
                          </td>
                          <td className="px-5 py-3 text-right font-bold text-green-600">
                            {formatCurrency(account.schedule.installments.reduce((sum, i) => sum + i.amountPaid, 0))}
                          </td>
                          <td className="px-5 py-3 text-right font-bold text-amber-600">
                            {formatCurrency(account.schedule.installments.reduce((sum, i) => sum + (i.amountDue - i.amountPaid), 0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                </div>
              ) : (
                /* No Schedule — the student can still submit a bank transfer */
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun échéancier assigné</h3>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto">
                    Un échéancier de paiement n'a pas encore été assigné à votre compte.
                    Vous pouvez néanmoins soumettre un virement bancaire dès maintenant.
                  </p>
                  {account.balance > 0 && (
                    <button
                      onClick={() => handleUploadClick(account.id)}
                      className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all"
                    >
                      <Upload className="w-4 h-4" />
                      Soumettre un virement
                    </button>
                  )}
                </div>
              )}

              {/* ========== SECTION 3: PENDING TRANSFERS ========== */}
              {pendingTransfers.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Virements en attente de validation</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Montant</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Banque</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Justificatif</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {pendingTransfers.map((transfer) => (
                          <tr key={transfer.id} className="hover:bg-gray-50">
                            <td className="px-5 py-4 text-sm text-gray-900">{formatShortDate(transfer.transferDate)}</td>
                            <td className="px-5 py-4 text-sm font-semibold text-gray-900">{formatCurrency(transfer.amount)}</td>
                            <td className="px-5 py-4 text-sm text-gray-600">{transfer.bankName || '-'}</td>
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${getStatusColor(transfer.status)}`}>
                                {transfer.status === 'PENDING' && <Clock className="w-3 h-3" />}
                                {transfer.status === 'APPROVED' && <CheckCircle className="w-3 h-3" />}
                                {transfer.status === 'REJECTED' && <XCircle className="w-3 h-3" />}
                                {formatTransferStatus(transfer.status)}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              {transfer.receiptUrl && (
                                <a
                                  href={`${API_ORIGIN}${transfer.receiptUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  <Download className="w-4 h-4" />
                                  Voir
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pendingTransfers.some(t => t.status === 'REJECTED') && (
                    <div className="px-5 py-4 bg-red-50 border-t border-red-100">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-red-800">Certains virements ont été rejetés</p>
                          {pendingTransfers
                            .filter(t => t.status === 'REJECTED' && t.rejectionReason)
                            .map(t => (
                              <p key={t.id} className="text-xs text-red-600 mt-1">Raison: {t.rejectionReason}</p>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ========== SECTION 4: RECENT PAYMENTS ========== */}
              {account.payments && account.payments.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Paiements effectués</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Montant</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Méthode</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Reçu</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Encaissé par</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {account.payments.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50">
                            <td className="px-5 py-4 text-sm text-gray-900">{formatShortDate(payment.paymentDate)}</td>
                            <td className="px-5 py-4 text-sm font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                            <td className="px-5 py-4 text-sm text-gray-600">{formatPaymentMethod(payment.method)}</td>
                            <td className="px-5 py-4 text-sm">
                              {payment.status !== 'CANCELLED' ? (
                                <button
                                  onClick={() => handleDownloadReceipt(payment.id, payment.receiptNumber)}
                                  disabled={downloadingReceiptId === payment.id}
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60 disabled:cursor-wait"
                                  title="Télécharger le reçu"
                                >
                                  {downloadingReceiptId === payment.id ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</>
                                  ) : (
                                    <><Download className="w-4 h-4" /> Télécharger</>
                                  )}
                                </button>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-600">{payment.recordedByName || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* No Account State */
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {currentAccount?.message || 'Aucun compte financier'}
              </h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                {allAccounts.length > 0
                  ? "Les frais de l'année en cours ne sont pas encore configurés pour votre filière. Vos années précédentes restent consultables dans l'onglet Historique."
                  : "Votre compte financier sera créé automatiquement lors de votre inscription à une filière avec des frais de scolarité configurés."}
              </p>
            </div>
          )}
        </>
      )}

      {activeTab === 'history' && (
        <>
          {allAccounts.length > 0 ? (
            <div className="space-y-4">
              {allAccounts.map((acc) => {
                const yearName = acc.academicYear?.name || 'N/A';
                const yearPayments = paymentsData?.groupedByYear[yearName] || acc.payments || [];
                const isExpanded = expandedYears.includes(acc.id);

                return (
                  <div key={acc.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleYear(acc.id)}
                      className="w-full px-5 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${acc.academicYear?.isCurrent ? 'bg-blue-100' : 'bg-gray-100'}`}>
                            <Calendar className={`w-5 h-5 ${acc.academicYear?.isCurrent ? 'text-blue-600' : 'text-gray-500'}`} />
                          </div>
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{yearName}</span>
                              {acc.academicYear?.isCurrent && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                  En cours
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-gray-500">
                              {yearPayments.length} paiement{yearPayments.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="hidden md:flex items-center gap-6 text-right">
                            <div>
                              <p className="text-xs text-gray-500">Frais</p>
                              <p className="text-sm font-semibold text-gray-900">{formatCurrency(acc.totalDue)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Payé</p>
                              <p className="text-sm font-semibold text-green-600">{formatCurrency(acc.totalPaid)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Solde</p>
                              <p className={`text-sm font-semibold ${acc.balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                {formatCurrency(acc.balance)}
                              </p>
                            </div>
                          </div>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${acc.balance > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                            {acc.balance > 0 ? (
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                            ) : (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-200">
                        {/* Summary */}
                        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50">
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <p className="text-xs text-gray-500">Frais totaux</p>
                            <p className="text-lg font-bold text-gray-900">{formatCurrency(acc.totalDue)}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <p className="text-xs text-gray-500">Réductions</p>
                            <p className="text-lg font-bold text-purple-600">{formatCurrency(acc.totalDiscount)}</p>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <p className="text-xs text-gray-500">Payé</p>
                            <p className="text-lg font-bold text-green-600">{formatCurrency(acc.totalPaid)}</p>
                          </div>
                          <div className={`rounded-lg p-3 border ${acc.balance > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                            <p className={`text-xs ${acc.balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>Solde</p>
                            <p className={`text-lg font-bold ${acc.balance > 0 ? 'text-amber-700' : 'text-green-700'}`}>{formatCurrency(acc.balance)}</p>
                          </div>
                        </div>

                        {/* Schedule if available */}
                        {acc.schedule && acc.schedule.installments && acc.schedule.installments.length > 0 && (
                          <div className="px-5 py-4 border-t border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Échéancier</h4>
                            <div className="space-y-2">
                              {acc.schedule.installments.map((installment, index) => (
                                <div
                                  key={installment.id}
                                  className={`flex items-center justify-between p-3 rounded-lg ${
                                    installment.status === 'PAID'
                                      ? 'bg-green-50'
                                      : installment.status === 'OVERDUE'
                                      ? 'bg-red-50'
                                      : 'bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                      installment.status === 'PAID' ? 'bg-green-500 text-white' :
                                      installment.status === 'OVERDUE' ? 'bg-red-500 text-white' :
                                      'bg-gray-300 text-gray-700'
                                    }`}>
                                      {index + 1}
                                    </span>
                                    <div>
                                      <p className="font-medium text-gray-900 text-sm">{installment.label}</p>
                                      <p className="text-xs text-gray-500">{formatShortDate(installment.dueDate)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="font-semibold text-sm text-gray-900">{formatCurrency(installment.amountDue)}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getInstallmentStatusColor(installment.status)}`}>
                                      {getInstallmentStatusLabel(installment.status)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Payments */}
                        {yearPayments.length > 0 ? (
                          <div className="border-t border-gray-200">
                            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-700">Paiements</h4>
                            </div>
                            <table className="min-w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Méthode</th>
                                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reçu</th>
                                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Encaissé par</th>
                                  <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {yearPayments.map((payment: StudentPayment) => (
                                  <tr key={payment.id}>
                                    <td className="px-5 py-3 text-sm text-gray-900">{formatShortDate(payment.paymentDate)}</td>
                                    <td className="px-5 py-3 text-sm font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                                    <td className="px-5 py-3 text-sm text-gray-600">{formatPaymentMethod(payment.method)}</td>
                                    <td className="px-5 py-3 text-sm">
                                      {payment.status !== 'CANCELLED' ? (
                                        <button
                                          onClick={() => handleDownloadReceipt(payment.id, payment.receiptNumber)}
                                          disabled={downloadingReceiptId === payment.id}
                                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60 disabled:cursor-wait"
                                          title="Télécharger le reçu"
                                        >
                                          {downloadingReceiptId === payment.id ? (
                                            <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</>
                                          ) : (
                                            <><Download className="w-4 h-4" /> Télécharger</>
                                          )}
                                        </button>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-5 py-3 text-sm text-gray-600">{payment.recordedByName || '-'}</td>
                                    <td className="px-5 py-3">
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                        payment.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                        payment.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {payment.status === 'PAID' && <CheckCircle className="w-3 h-3" />}
                                        {payment.status === 'PAID' ? 'Validé' : payment.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="px-5 py-8 text-center border-t border-gray-200">
                            <Receipt className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">Aucun paiement pour cette année</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <History className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun historique</h3>
              <p className="text-sm text-gray-500">Votre historique financier apparaîtra ici.</p>
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedAccountId && (
        <BankTransferUploadModal
          accountId={selectedAccountId}
          balance={currentAccount?.account?.balance}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};
